/**
 * Per-viewer "already acknowledged" state for the onboarding pop-ups that
 * aren't tracked on the server: the PENDING "we're verifying" notice and the
 * REJECTED "we need a bit more" notice. The APPROVED welcome tour is tracked
 * server-side instead (PropertyOwner.onboardingSeenAt) so it fires once per
 * home across devices.
 *
 * Every read/write is wrapped so a private window, blocked storage or a
 * thumbnail capture never throws — at worst a pop-up shows one extra time,
 * which is acceptable for these two notices.
 */

const PENDING_PREFIX = "sh_onboard_pending_";
const REJECTED_PREFIX = "sh_onboard_rejected_";

const read = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — the notice may show again, which is fine */
  }
};

// PENDING is keyed by property + the claim's submission time, so a brand-new
// claim (e.g. a re-submit after a rejection) shows the notice again.
export const pendingKey = (propertyId: string, createdAt?: string | null): string =>
  `${PENDING_PREFIX}${propertyId}:${createdAt || ""}`;

export const isPendingAcked = (propertyId: string, createdAt?: string | null): boolean =>
  read(pendingKey(propertyId, createdAt)) === "1";

export const ackPending = (propertyId: string, createdAt?: string | null): void =>
  write(pendingKey(propertyId, createdAt), "1");

// REJECTED is keyed by the claim + when it was rejected, so a fresh rejection
// (a new reason after a re-submit) surfaces again.
export const rejectedKey = (apnId?: string | null, statusUpdatedAt?: string | null): string =>
  `${REJECTED_PREFIX}${apnId || ""}:${statusUpdatedAt || ""}`;

export const isRejectedAcked = (apnId?: string | null, statusUpdatedAt?: string | null): boolean =>
  read(rejectedKey(apnId, statusUpdatedAt)) === "1";

export const ackRejected = (apnId?: string | null, statusUpdatedAt?: string | null): void =>
  write(rejectedKey(apnId, statusUpdatedAt), "1");
